import { useState } from 'react'
import axios from 'axios'
import { PauseCircle, PlayCircle, Download, Archive, X, ShieldAlert } from 'lucide-react'
import {
  suspendTenant,
  resumeTenant,
  exportTenant,
  offboardTenant,
  type TenantExportPackage,
} from '../lib/api'
import { cn } from '../lib/utils'
import type { Tenant } from '../types'

// Calm admin controls over the LIVE tenant-lifecycle routes
// (POST /admin/tenants/{id}/suspend|resume|offboard, GET .../export).
//
// Voice: no fake success. Every action calls the real backend; a failure
// surfaces an honest error and the row's status is left exactly as it was.
// The available actions follow the backend's fail-closed state machine:
//   active/onboarding → Suspend · Offboard · Export
//   suspended         → Resume  · Offboard · Export
//   offboarding/deleted (terminal) → Export only
// Offboard is guarded twice: the admin must TYPE the tenant id to confirm,
// and the id is echoed to the backend which fails closed (400) on mismatch.

type ActionKind = 'suspend' | 'resume' | 'offboard' | 'export'

function errorMessage(err: unknown, fallback: string): string {
  if (axios.isAxiosError(err)) {
    const detail = err.response?.data?.detail
    if (typeof detail === 'string' && detail) return detail
  }
  return fallback
}

interface Props {
  tenant: Tenant
  // Called with the tenant's new status after a successful transition so the
  // parent can reflect it without a refetch. Export does not change status.
  onStatusChange: (tenantId: string, status: string) => void
}

export function TenantLifecycleControls({ tenant, onStatusChange }: Props) {
  const [action, setAction] = useState<ActionKind | null>(null)
  const [reason, setReason] = useState('')
  const [confirmId, setConfirmId] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [exported, setExported] = useState<TenantExportPackage | null>(null)

  const terminal =
    tenant.status === 'offboarding' || tenant.status === 'deleted'
  const canSuspend = tenant.status === 'active' || tenant.status === 'onboarding'
  const canResume = tenant.status === 'suspended'

  function open(kind: ActionKind) {
    setAction(kind)
    setReason('')
    setConfirmId('')
    setError(null)
    setExported(null)
  }

  function close() {
    if (busy) return
    setAction(null)
  }

  async function run() {
    setBusy(true)
    setError(null)
    try {
      if (action === 'suspend') {
        const res = await suspendTenant(tenant.id, reason || undefined)
        onStatusChange(tenant.id, res.status)
        setAction(null)
      } else if (action === 'resume') {
        const res = await resumeTenant(tenant.id, reason || undefined)
        onStatusChange(tenant.id, res.status)
        setAction(null)
      } else if (action === 'offboard') {
        const res = await offboardTenant(
          tenant.id,
          confirmId.trim(),
          reason || undefined,
        )
        onStatusChange(tenant.id, res.status)
        setAction(null)
      } else if (action === 'export') {
        const pkg = await exportTenant(tenant.id)
        setExported(pkg)
      }
    } catch (err) {
      setError(
        errorMessage(
          err,
          `Could not ${action} this tenant. Nothing was changed.`,
        ),
      )
    } finally {
      setBusy(false)
    }
  }

  function downloadExport() {
    if (!exported) return
    // jsdom (tests) has no object-URL/anchor plumbing — guard so the control
    // degrades gracefully rather than throwing.
    if (typeof URL.createObjectURL !== 'function') return
    const blob = new Blob([JSON.stringify(exported, null, 2)], {
      type: 'application/json',
    })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `tenant-${tenant.slug}-export.json`
    document.body.appendChild(a)
    a.click()
    a.remove()
    URL.revokeObjectURL(url)
  }

  const btn =
    'tap-min inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-medium border transition-colors disabled:opacity-50'

  return (
    <div className="flex items-center gap-2 justify-end">
      {canSuspend && (
        <button
          type="button"
          onClick={() => open('suspend')}
          className={cn(btn, 'border-warning/40 text-warning hover:bg-warning/10')}
        >
          <PauseCircle className="w-3.5 h-3.5" aria-hidden="true" />
          Suspend
        </button>
      )}
      {canResume && (
        <button
          type="button"
          onClick={() => open('resume')}
          className={cn(btn, 'border-success/40 text-success hover:bg-success/10')}
        >
          <PlayCircle className="w-3.5 h-3.5" aria-hidden="true" />
          Resume
        </button>
      )}
      <button
        type="button"
        onClick={() => open('export')}
        className={cn(btn, 'border-gray-300 text-gray-600 hover:bg-gray-50')}
      >
        <Download className="w-3.5 h-3.5" aria-hidden="true" />
        Export
      </button>
      {!terminal && (
        <button
          type="button"
          onClick={() => open('offboard')}
          className={cn(btn, 'border-danger/40 text-danger hover:bg-danger/10')}
        >
          <Archive className="w-3.5 h-3.5" aria-hidden="true" />
          Offboard
        </button>
      )}

      {action && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-navy/40 p-4"
          role="dialog"
          aria-modal="true"
          aria-label={`${action} tenant ${tenant.name}`}
          onClick={close}
        >
          <div
            className="w-full max-w-md rounded-2xl bg-white p-5 shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between mb-3">
              <h3 className="text-base font-semibold text-navy capitalize">
                {action} {tenant.name}
              </h3>
              <button
                type="button"
                onClick={close}
                disabled={busy}
                aria-label="Close"
                className="tap-min text-gray-400 hover:text-navy disabled:opacity-50"
              >
                <X className="w-4 h-4" aria-hidden="true" />
              </button>
            </div>

            {/* Export: a metadata inventory only — never secrets. If the
                package ever claims to carry credentials, refuse to render it —
                the FE will not become a secret-leaking surface. */}
            {action === 'export' && exported && exported.contains_credentials ? (
              <div className="space-y-3 text-sm" role="alert">
                <div className="flex items-start gap-2 rounded-lg border border-danger/30 bg-danger-light p-3 text-danger">
                  <ShieldAlert className="w-4 h-4 shrink-0 mt-0.5" aria-hidden="true" />
                  <p>
                    This export claims to contain credentials
                    (<span className="font-mono text-xs">contains_credentials=true</span>).
                    Refusing to display it — a tenant export must never carry
                    secrets. Nothing is shown.
                  </p>
                </div>
                <div className="flex justify-end pt-1">
                  <button
                    type="button"
                    onClick={close}
                    className="tap-min px-3 py-2 rounded-lg text-sm text-gray-600 hover:bg-gray-50"
                  >
                    Close
                  </button>
                </div>
              </div>
            ) : action === 'export' && exported ? (
              <div className="space-y-3 text-sm">
                <p className="text-gray-600">
                  Metadata inventory for{' '}
                  <span className="font-mono text-xs">{exported.tenant.slug}</span>.
                  Contains no credentials or secrets.
                </p>
                <dl className="grid grid-cols-2 gap-2">
                  {(
                    [
                      ['Principals', exported.inventory.principals],
                      ['Accounts on file', exported.inventory.monitored_accounts],
                      ['Cases', exported.inventory.cases],
                      ['Alerts', exported.inventory.alerts],
                    ] as const
                  ).map(([label, value]) => (
                    <div
                      key={label}
                      className="rounded-lg border border-gray-200 p-2.5"
                    >
                      <dt className="text-xs text-gray-500">{label}</dt>
                      <dd className="text-lg font-bold text-navy">{value}</dd>
                    </div>
                  ))}
                </dl>
                <div className="flex justify-end gap-2 pt-1">
                  <button
                    type="button"
                    onClick={close}
                    className="tap-min px-3 py-2 rounded-lg text-sm text-gray-600 hover:bg-gray-50"
                  >
                    Close
                  </button>
                  <button
                    type="button"
                    onClick={downloadExport}
                    className="tap-min inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-brass text-white text-sm font-medium hover:bg-brass-dark"
                  >
                    <Download className="w-4 h-4" aria-hidden="true" />
                    Download JSON
                  </button>
                </div>
              </div>
            ) : (
              <div className="space-y-3">
                {action === 'export' ? (
                  <p className="text-sm text-gray-600">
                    Export a metadata inventory (counts only — never credentials
                    or secrets) for this tenant.
                  </p>
                ) : action === 'offboard' ? (
                  <p className="text-sm text-gray-600">
                    Offboarding stops all ingestion, disables every account on
                    file, and blocks client login. It is reversible (a soft
                    state, not a delete). To confirm, type the tenant id below.
                  </p>
                ) : action === 'suspend' ? (
                  <p className="text-sm text-gray-600">
                    Suspending stops ingestion and blocks client login. Data is
                    retained and this is reversible via Resume.
                  </p>
                ) : (
                  <p className="text-sm text-gray-600">
                    Resume returns this suspended tenant to active.
                  </p>
                )}

                {action !== 'export' && (
                  <label className="block">
                    <span className="text-xs font-medium text-gray-600">
                      Reason (optional, recorded in the audit trail)
                    </span>
                    <textarea
                      value={reason}
                      onChange={(e) => setReason(e.target.value)}
                      rows={2}
                      maxLength={1024}
                      className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-navy focus:outline-none focus:ring-2 focus:ring-brass"
                    />
                  </label>
                )}

                {action === 'offboard' && (
                  <label className="block">
                    <span className="text-xs font-medium text-gray-600">
                      Type the tenant id to confirm
                    </span>
                    <input
                      type="text"
                      value={confirmId}
                      onChange={(e) => setConfirmId(e.target.value)}
                      placeholder={tenant.id}
                      aria-label="Confirm tenant id"
                      className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 font-mono text-xs text-navy focus:outline-none focus:ring-2 focus:ring-danger"
                    />
                    <span className="mt-1 block font-mono text-[11px] text-gray-400 break-all">
                      {tenant.id}
                    </span>
                  </label>
                )}

                {error && (
                  <p className="text-sm text-danger" role="alert">
                    {error}
                  </p>
                )}

                <div className="flex justify-end gap-2 pt-1">
                  <button
                    type="button"
                    onClick={close}
                    disabled={busy}
                    className="tap-min px-3 py-2 rounded-lg text-sm text-gray-600 hover:bg-gray-50 disabled:opacity-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={run}
                    disabled={
                      busy ||
                      (action === 'offboard' && confirmId.trim() !== tenant.id)
                    }
                    className={cn(
                      'tap-min px-3 py-2 rounded-lg text-sm font-medium text-white disabled:opacity-50',
                      action === 'offboard'
                        ? 'bg-danger hover:bg-danger/90'
                        : action === 'suspend'
                          ? 'bg-warning hover:bg-warning/90'
                          : 'bg-brass hover:bg-brass-dark',
                    )}
                  >
                    {busy
                      ? 'Working…'
                      : action === 'export'
                        ? 'Export'
                        : action.charAt(0).toUpperCase() + action.slice(1)}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
